import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context";

export function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/dashboard");
    } catch {
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="section">
      <div className="container" style={{ maxWidth: "440px" }}>
        <div className="card" style={{ padding: "2rem" }}>
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <h1 className="display-4" style={{ marginBottom: "0.25rem" }}>Create Account</h1>
            <p className="text-muted">Join the Aekam community</p>
          </div>

          {error && (
            <div className="alert alert--error" role="alert" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="contact-form__row">
              <div className="form-group">
                <label className="form-label" htmlFor="reg-first">First Name *</label>
                <input id="reg-first" name="firstName" type="text" className="form-input"
                  required value={form.firstName} onChange={handleChange} autoComplete="given-name" />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="reg-last">Last Name *</label>
                <input id="reg-last" name="lastName" type="text" className="form-input"
                  required value={form.lastName} onChange={handleChange} autoComplete="family-name" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="reg-email">Email *</label>
              <input id="reg-email" name="email" type="email" className="form-input"
                required value={form.email} onChange={handleChange} autoComplete="email" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="reg-pw">Password *</label>
              <input id="reg-pw" name="password" type="password" className="form-input"
                required value={form.password} onChange={handleChange} autoComplete="new-password"
                minLength={8} />
              <p style={{ fontSize: "0.78rem", color: "var(--c-ink-75)", marginTop: "0.25rem" }}>
                Minimum 8 characters
              </p>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="reg-confirm">Confirm Password *</label>
              <input id="reg-confirm" name="confirm" type="password" className="form-input"
                required value={form.confirm} onChange={handleChange} autoComplete="new-password" />
            </div>

            <button type="submit" className="btn btn--brand btn--lg" style={{ width: "100%", marginTop: "0.5rem" }}
              disabled={loading}>
              {loading ? "Creating account…" : "Create Account"}
            </button>
          </form>

          <p style={{ textAlign: "center", marginTop: "1.25rem", fontSize: "0.9rem", color: "var(--c-ink-75)" }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "var(--c-brand)" }}>Sign in</Link>
          </p>
        </div>
      </div>
    </section>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <section className="section">
      <div className="container" style={{ maxWidth: "440px" }}>
        <div className="card" style={{ padding: "2rem" }}>
          {submitted ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📧</div>
              <h2>Check your email</h2>
              <p className="text-muted" style={{ marginTop: "0.5rem" }}>
                If an account exists for <strong>{email}</strong>, we sent a password reset link.
              </p>
              <Link to="/login" className="btn btn--outline" style={{ marginTop: "1.5rem" }}>Back to Login</Link>
            </div>
          ) : (
            <>
              <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                <h1 className="display-4">Forgot Password</h1>
                <p className="text-muted">Enter your email to receive a reset link.</p>
              </div>
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label className="form-label" htmlFor="fp-email">Email *</label>
                  <input id="fp-email" type="email" className="form-input"
                    required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <button type="submit" className="btn btn--brand btn--lg" style={{ width: "100%", marginTop: "0.5rem" }}>
                  Send Reset Link
                </button>
              </form>
              <p style={{ textAlign: "center", marginTop: "1.25rem", fontSize: "0.9rem", color: "var(--c-ink-75)" }}>
                <Link to="/login" style={{ color: "var(--c-brand)" }}>Back to Login</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export function ResetPasswordPage() {
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError("");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setDone(true);
    setTimeout(() => navigate("/login"), 2500);
  }

  return (
    <section className="section">
      <div className="container" style={{ maxWidth: "440px" }}>
        <div className="card" style={{ padding: "2rem" }}>
          {done ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✅</div>
              <h2>Password updated!</h2>
              <p className="text-muted">Redirecting to login…</p>
            </div>
          ) : (
            <>
              <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                <h1 className="display-4">Reset Password</h1>
                <p className="text-muted">Choose a new secure password.</p>
              </div>
              {error && (
                <div className="alert alert--error" style={{ marginBottom: "1rem" }}>{error}</div>
              )}
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label className="form-label" htmlFor="rp-pw">New Password *</label>
                  <input id="rp-pw" name="password" type="password" className="form-input"
                    required value={form.password} onChange={handleChange} minLength={8} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="rp-confirm">Confirm Password *</label>
                  <input id="rp-confirm" name="confirm" type="password" className="form-input"
                    required value={form.confirm} onChange={handleChange} />
                </div>
                <button type="submit" className="btn btn--brand btn--lg" style={{ width: "100%", marginTop: "0.5rem" }}>
                  Update Password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
